// ChapterCheckInTile.tsx — the "how are you managing?" check-in as a TILE (not a
// full screen). Shown on the chapter-complete celebration, only at the end of the
// main (core) teaching chapters — never after intro/summary or skill chapters.
// Three honest options; each reveals tailored, compassionate guidance in place.
import React from 'react';
import { Display, Body, Pressable } from '@/components/ui';
import { Icon, type IconName } from '@/components/icons';

type Mood = 'learning' | 'confused' | 'upset';

const OPTIONS: { id: Mood; icon: IconName; label: string; tone: 'green' | 'amber' | 'rose' }[] = [
  { id: 'learning', icon: 'sparkle', label: 'Learning', tone: 'green' },
  { id: 'confused', icon: 'clock', label: 'Confused', tone: 'amber' },
  { id: 'upset', icon: 'heart', label: 'Upset', tone: 'rose' },
];

const TONE: Record<string, string> = { green: 'var(--green-deep)', amber: 'var(--amber-deep)', rose: '#b06a58' };
const TONE_BG: Record<string, string> = {
  green: 'rgba(29,158,117,0.12)',
  amber: 'rgba(200,134,26,0.12)',
  rose: 'rgba(176,106,88,0.12)',
};

const GUIDANCE: Record<Mood, { title: { pre: string; em: string }; body: string }> = {
  learning: {
    title: { pre: 'Keep ', em: 'going' },
    body: 'That is wonderful. Keep going — but be careful not to work too hard or too fast. The brain needs time to heal, so let what you are learning settle.',
  },
  confused: {
    title: { pre: 'That is ', em: 'okay' },
    body: 'Take time to relisten if you need to, and to complete your journalling. Some time off the app can help — the brain learns a lot during sleep and on autopilot. Come back in a few days; the penny can drop later.',
  },
  upset: {
    title: { pre: 'Be gentle with ', em: 'yourself' },
    body: 'Recovery is not about speed; it is about listening to your body and giving yourself what you need. Two kinder routes you can take instead:',
  },
};

export function ChapterCheckInTile({
  onCalmYourBody,
  onSelfCompassion,
}: {
  onCalmYourBody?: () => void;
  onSelfCompassion?: () => void;
}) {
  const [mood, setMood] = React.useState<Mood | null>(null);

  const routeBtn = (icon: IconName, label: string, onClick: () => void) => (
    <Pressable
      as="button"
      onClick={onClick}
      ariaLabel={label}
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8, width: '100%',
        padding: '12px 16px', borderRadius: 13, border: '1px solid rgba(29,158,117,0.4)',
        background: 'rgba(29,158,117,0.06)', color: 'var(--green-deep)', cursor: 'pointer',
        fontFamily: 'Inter, system-ui, sans-serif', fontSize: 14, fontWeight: 600,
      }}
    >
      <Icon name={icon} size={16} /> {label}
    </Pressable>
  );

  return (
    <div
      style={{
        textAlign: 'left',
        padding: '16px 16px 17px',
        borderRadius: 18,
        border: '1px solid var(--hair)',
        background: 'linear-gradient(180deg, var(--card2-top, var(--card-top)), var(--card2-bot, var(--card-bot)))',
        boxShadow: 'var(--edge), var(--sh1)',
      }}
    >
      {mood === null ? (
        <>
          <Display parts={{ pre: 'How are you ', em: 'managing', post: '?' }} size={19} lh={1.16} />
          <Body dim size={12.5} style={{ margin: '6px 0 13px' }}>
            There’s no right answer — whatever you choose helps us look after you.
          </Body>
          <div style={{ display: 'flex', gap: 8 }}>
            {OPTIONS.map((o) => (
              <Pressable
                key={o.id}
                as="button"
                onClick={() => setMood(o.id)}
                ariaLabel={o.label}
                style={{
                  flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 7,
                  padding: '13px 6px', borderRadius: 14, border: '1px solid var(--hair)',
                  background: 'var(--card-top)', cursor: 'pointer',
                }}
              >
                <span style={{ width: 38, height: 38, borderRadius: 11, display: 'flex', alignItems: 'center', justifyContent: 'center', background: TONE_BG[o.tone], color: TONE[o.tone] }}>
                  <Icon name={o.icon} size={19} />
                </span>
                <span style={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, fontWeight: 600, color: 'var(--ink)' }}>
                  {o.label}
                </span>
              </Pressable>
            ))}
          </div>
        </>
      ) : (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
            <span style={{ width: 38, height: 38, borderRadius: 11, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: TONE_BG[OPTIONS.find((o) => o.id === mood)!.tone], color: TONE[OPTIONS.find((o) => o.id === mood)!.tone] }}>
              <Icon name={OPTIONS.find((o) => o.id === mood)!.icon} size={19} />
            </span>
            <Display parts={GUIDANCE[mood].title} size={18} lh={1.15} />
          </div>
          <Body size={13.5} style={{ lineHeight: 1.55 }}>{GUIDANCE[mood].body}</Body>
          {mood === 'upset' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 9, marginTop: 13 }}>
              {onCalmYourBody && routeBtn('leaf', 'Open Calm Your Body', onCalmYourBody)}
              {onSelfCompassion && routeBtn('heart', 'Move to self-compassion', onSelfCompassion)}
            </div>
          )}
          <Pressable
            as="button"
            onClick={() => setMood(null)}
            ariaLabel="Change my answer"
            style={{
              marginTop: 12, padding: 0, background: 'none', border: 'none', cursor: 'pointer',
              color: 'var(--ink-soft)', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 12.5, fontWeight: 600,
            }}
          >
            ← Change my answer
          </Pressable>
        </>
      )}
    </div>
  );
}
